<template>
    <div class="notice-create-page editor-wrapper px-4 py-4 md:px-5">
        <div class="mx-auto space-y-5">
            <section class="rounded-2xl border border-slate-200 bg-white p-5 shadow-sm">
                <div class="flex flex-col gap-4 md:flex-row md:items-start md:justify-between">
                    <div>
                        <p class="text-[15px] font-semibold text-slate-900">
                            {{ noticePage.noticeUpdate ? "공지사항 수정" : "공지사항 등록" }}
                        </p>
                        <p class="mt-1 text-sm text-slate-500">공지사항 제목과 내용을 입력합니다.</p>
                    </div>

                    <span class="result-chip">
                        {{ noticePage.noticeUpdate ? "수정 모드" : "등록 모드" }}
                    </span>
                </div>
            </section>

            <section class="rounded-2xl border border-slate-200 bg-white p-5 shadow-sm">
                <div class="mb-4 border-b border-slate-200 pb-4">
                    <p class="text-base font-semibold text-slate-900">기본 정보</p>
                    <p class="mt-1 text-sm text-slate-500">목록에 노출될 공지사항 제목입니다.</p>
                </div>

                <label class="form-field">
                    <span class="form-label">제목</span>
                    <input
                        v-model="noticePage.noticeList.title"
                        type="text"
                        maxlength="50"
                        placeholder="제목을 입력하세요."
                        class="form-control"
                    />
                </label>
            </section>

            <section class="rounded-2xl border border-slate-200 bg-white p-5 shadow-sm">
                <div class="mb-4 border-b border-slate-200 pb-4">
                    <p class="text-base font-semibold text-slate-900">내용</p>
                    <p class="mt-1 text-sm text-slate-500">공지사항 상세 내용을 작성합니다.</p>
                </div>

                <div class="editor-card">
                    <BagicEditor v-model="noticePage.noticeList.content" etype="notice"></BagicEditor>
                </div>
            </section>

            <!-- 파일 첨부 -->
            <section class="rounded-2xl border border-slate-200 bg-white p-5 shadow-sm">
                <div class="mb-4 border-b border-slate-200 pb-4">
                    <p class="text-base font-semibold text-slate-900">파일 첨부</p>
                    <p class="mt-1 text-sm text-slate-500">최대 3개까지 첨부할 수 있습니다.</p>
                </div>

                <div class="flex flex-col gap-3">
                    <div
                        v-for="(file, idx) in noticePage.attachments"
                        :key="idx"
                        class="attach-item"
                    >
                        <div class="attach-item__info">
                            <svg class="attach-icon" xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
                                <path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z"/>
                                <polyline points="14,2 14,8 20,8"/>
                            </svg>
                            <span v-if="file.uploading" class="attach-name uploading">업로드 중...</span>
                            <span v-else-if="file.url" class="attach-name">{{ file.name }}</span>
                            <span v-else class="attach-name empty">파일 {{ idx + 1 }} — 미첨부</span>
                            <span v-if="file.size" class="attach-size">{{ formatFileSize(file.size) }}</span>
                        </div>
                        <div class="attach-actions">
                            <!-- 다운로드 (파일 있을 때만) -->
                            <button
                                v-if="file.url && !file.uploading"
                                type="button"
                                class="attach-download-btn"
                                title="다운로드"
                                @click="noticePage.downloadFile(file)"
                            >
                                <svg xmlns="http://www.w3.org/2000/svg" width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
                                    <path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4"/>
                                    <polyline points="7 10 12 15 17 10"/>
                                    <line x1="12" y1="15" x2="12" y2="3"/>
                                </svg>
                            </button>
                            <!-- 파일 교체 -->
                            <label class="attach-change-btn" title="파일 선택">
                                <svg xmlns="http://www.w3.org/2000/svg" width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
                                    <line x1="12" y1="5" x2="12" y2="19"/><line x1="5" y1="12" x2="19" y2="12"/>
                                </svg>
                                <input type="file" class="sr-only" @change="(e) => noticePage.addAttachment(e, idx)" />
                            </label>
                            <!-- 삭제 (파일 있을 때만) -->
                            <button
                                v-if="file.url || file.uploading"
                                type="button"
                                class="attach-remove"
                                @click="noticePage.removeAttachment(idx)"
                            >
                                <svg xmlns="http://www.w3.org/2000/svg" width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
                                    <line x1="18" y1="6" x2="6" y2="18"/><line x1="6" y1="6" x2="18" y2="18"/>
                                </svg>
                            </button>
                        </div>
                    </div>
                </div>
            </section>

            <section class="rounded-2xl border border-slate-200 bg-white p-5 shadow-sm">
                <div class="flex flex-col gap-4 md:flex-row md:items-center md:justify-between">
                    <p class="text-sm text-slate-500">작성한 내용은 저장 후 공지사항 목록에서 확인할 수 있습니다.</p>
                    <div class="flex flex-col gap-3 sm:flex-row">
                        <button type="button" class="secondary-button" @click="noticePage.goBack">돌아가기</button>
                        <button
                            v-if="!noticePage.noticeUpdate"
                            type="button"
                            class="primary-button"
                            @click="noticePage.registNotice"
                        >
                            등록
                        </button>
                        <button
                            v-if="noticePage.noticeUpdate"
                            type="button"
                            class="primary-button"
                            @click="noticePage.registNotice"
                        >
                            수정
                        </button>
                    </div>
                </div>
            </section>
        </div>
    </div>
</template>
<script>
import { useRoute } from "vue-router";
import BagicEditor from "@/components/BagicEditor.vue";
import { reactive, onMounted } from "vue";
import axios from "@/plugins/axios.js";

function formatFileSize(bytes) {
    if (!bytes) return "";
    if (bytes < 1024) return bytes + " B";
    if (bytes < 1024 * 1024) return (bytes / 1024).toFixed(1) + " KB";
    return (bytes / (1024 * 1024)).toFixed(1) + " MB";
}

export default {
    components: {
        BagicEditor,
    },
    setup() {
        const route = useRoute();

        const noticePage = reactive({
            noticeList: {
                content: "",
                idxLeSiteMain: "",
                idxNotice: "",
                title: "",
                viewCount: 0,
            },

            noticeUpdate: false,

            // 파일 첨부 슬롯 3개 고정 { name, size, url, uploading }
            attachments: [
                { name: "", size: 0, url: "", uploading: false },
                { name: "", size: 0, url: "", uploading: false },
                { name: "", size: 0, url: "", uploading: false },
            ],

            get attachmentCount() {
                return noticePage.attachments.filter((f) => f.url).length;
            },

            async addAttachment(e, slotIdx) {
                const file = e.target.files[0];
                e.target.value = "";
                if (!file) return;

                const slot = noticePage.attachments[slotIdx];
                slot.name = file.name;
                slot.size = file.size;
                slot.url = "";
                slot.uploading = true;

                const formData = new FormData();
                formData.append("file", file);
                formData.append("etype", "notice_file");
                try {
                    const res = await axios.post("/admin/upload", formData);
                    if (res.data.success) {
                        slot.url = res.data.data.uploadUrl;
                        slot.name = res.data.data.fileName || file.name;
                    } else {
                        slot.name = "";
                        slot.size = 0;
                        slot.url = "";
                    }
                } catch {
                    slot.name = "";
                    slot.size = 0;
                    slot.url = "";
                } finally {
                    slot.uploading = false;
                }
            },

            removeAttachment(slotIdx) {
                const slot = noticePage.attachments[slotIdx];
                slot.name = "";
                slot.size = 0;
                slot.url = "";
                slot.uploading = false;
            },

            // 공지사항 조회
            getRegistNotice() {
                noticePage.noticeList.idxNotice = route.query.idxNotice;
                if (
                    noticePage.noticeList.idxNotice &&
                    noticePage.noticeList.idxNotice !== "0"
                ) {
                    const params = {
                        idxNotice: noticePage.noticeList.idxNotice,
                    };
                    axios.get(`/admin/notice`, { params }).then(function (res) {
                        if (res.data.success) {
                            noticePage.noticeList = res.data.data;
                            noticePage.noticeUpdate = true;
                            // 첨부파일 슬롯 복원
                            const d = res.data.data;
                            [1, 2, 3].forEach((n) => {
                                noticePage.attachments[n - 1] = {
                                    name: d[`fileName${n}`] || "",
                                    size: 0,
                                    url: d[`fileUrl${n}`] || "",
                                    uploading: false,
                                };
                            });
                        }
                    });
                }
            },

            // 공지사항 등록
            registNotice() {
                if (!noticePage.noticeList.title || !noticePage.noticeList.title.trim()) {
                    alert("제목을 입력해주세요.");
                    return;
                }
                noticePage.noticeList.idxLeSiteMain = route.query.idxLeSiteMain;
                noticePage.noticeList.idxNotice = route.query.idxNotice;
                const a = noticePage.attachments;
                let params = {
                    ...noticePage.noticeList,
                    fileUrl1: a[0].url || "",
                    fileName1: a[0].url ? a[0].name : "",
                    fileUrl2: a[1].url || "",
                    fileName2: a[1].url ? a[1].name : "",
                    fileUrl3: a[2].url || "",
                    fileName3: a[2].url ? a[2].name : "",
                };
                axios.post("/admin/notice", params, {}).then(function (res) {
                    if (res.data.success) {
                        if (noticePage.noticeUpdate) {
                            alert("수정되었습니다.");
                            window.history.back();
                        } else if (noticePage.noticeList.idxNotice !== "0") {
                            alert("등록되었습니다.");
                            window.history.back();
                        }
                    } else {
                        //alert(res.data.message);
                    }
                    // listPage.is_loader = false;
                });
            },

            async downloadFile(file) {
                try {
                    const res = await fetch(file.url);
                    const blob = await res.blob();
                    const blobUrl = URL.createObjectURL(blob);
                    const a = document.createElement("a");
                    a.href = blobUrl;
                    a.download = file.name;
                    a.click();
                    URL.revokeObjectURL(blobUrl);
                } catch {
                    window.open(file.url, "_blank");
                }
            },

            goBack() {
                window.history.back();
            },
        });

        onMounted(() => {
            noticePage.getRegistNotice();
        });

        return {
            noticePage,
            formatFileSize,
        };
    },
};
</script>
<style scoped>
.notice-create-page {
    min-height: 100%;
    background:
        radial-gradient(circle at top left, rgba(226, 232, 240, 0.65), transparent 32rem),
        linear-gradient(180deg, #f8fafc 0%, #ffffff 42%);
}

.result-chip {
    display: inline-flex;
    align-items: center;
    border-radius: 9999px;
    border: 1px solid #e2e8f0;
    background: #f8fafc;
    padding: 0.4rem 0.8rem;
}

.form-field {
    display: flex;
    flex-direction: column;
    gap: 0.5rem;
}

.form-label {
    font-size: 0.75rem;
    font-weight: 700;
    color: #64748b;
}

.form-control {
    height: 2.875rem;
    width: 100%;
    border: 1px solid #cbd5e1;
    border-radius: 0.75rem;
    background-color: #fff;
    padding: 0.7rem 0.9rem;
    font-size: 0.95rem;
    color: #0f172a;
    outline: none;
    transition:
        border-color 0.2s ease,
        box-shadow 0.2s ease;
}

.form-control:focus {
    border-color: #94a3b8;
    box-shadow: 0 0 0 3px rgba(148, 163, 184, 0.15);
}

.editor-card {
    border-radius: 0.75rem;
    border: 1px solid #e2e8f0;
}

.primary-button,
.secondary-button {
    display: inline-flex;
    min-height: 2.875rem;
    align-items: center;
    justify-content: center;
    border-radius: 0.75rem;
    padding: 0.7rem 1.1rem;
    font-size: 0.95rem;
    font-weight: 700;
    transition:
        background-color 0.2s ease,
        border-color 0.2s ease,
        color 0.2s ease;
}

.primary-button {
    background: #2563eb;
    color: #fff;
}

.primary-button:hover {
    background: #1d4ed8;
}

.secondary-button {
    border: 1px solid #cbd5e1;
    background: #f8fafc;
    color: #475569;
}

.secondary-button:hover {
    background: #f1f5f9;
}

.editor-wrapper :deep(.tox) {
    height: 1000px !important;
}

.attach-item {
    display: flex;
    align-items: center;
    justify-content: space-between;
    padding: 0.6rem 0.875rem;
    border: 1px solid #e2e8f0;
    border-radius: 0.75rem;
    background: #f8fafc;
}

.attach-item__info {
    display: flex;
    align-items: center;
    gap: 0.5rem;
    min-width: 0;
}

.attach-icon {
    width: 16px;
    height: 16px;
    flex-shrink: 0;
    color: #64748b;
}

.attach-name {
    font-size: 0.875rem;
    color: #1e293b;
    white-space: nowrap;
    overflow: hidden;
    text-overflow: ellipsis;
    max-width: 300px;
}

.attach-name.uploading {
    color: #94a3b8;
    font-style: italic;
}

.attach-size {
    font-size: 0.75rem;
    color: #94a3b8;
    flex-shrink: 0;
}

.attach-remove {
    display: flex;
    align-items: center;
    justify-content: center;
    width: 24px;
    height: 24px;
    border: none;
    border-radius: 50%;
    background: transparent;
    color: #94a3b8;
    cursor: pointer;
    flex-shrink: 0;
    transition: background 0.15s, color 0.15s;
}

.attach-remove:hover {
    background: #fee2e2;
    color: #ef4444;
}

.attach-actions {
    display: flex;
    align-items: center;
    gap: 0.25rem;
    flex-shrink: 0;
}

.attach-change-btn {
    display: flex;
    align-items: center;
    justify-content: center;
    width: 26px;
    height: 26px;
    border-radius: 5px;
    background: transparent;
    color: #64748b;
    cursor: pointer;
    transition: background 0.15s, color 0.15s;
}

.attach-change-btn:hover {
    background: #eff6ff;
    color: #2563eb;
}

.attach-name.empty {
    color: #cbd5e1;
    font-style: italic;
}

.attach-download-btn {
    display: flex;
    align-items: center;
    justify-content: center;
    width: 26px;
    height: 26px;
    border-radius: 5px;
    background: transparent;
    color: #64748b;
    cursor: pointer;
    transition: background 0.15s, color 0.15s;
    text-decoration: none;
}

.attach-download-btn:hover {
    background: #f0fdf4;
    color: #16a34a;
}
</style>
